import { AnimatePresence, motion } from 'framer-motion';
import React, { useState } from 'react';
import { Oval } from 'react-loader-spinner';
import styled from 'styled-components';
import { ReactComponent as CloseIcon } from '@/assets/icons/close.icon.svg';
import { device } from '@/constants/breakpoints';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import OTPInput from 'react-otp-input';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { appSelector } from '@/redux/features/app.slice';
import { InvoiceDiscountMakePayment } from '@/services/financingApi';
import { useNavigate } from 'react-router-dom';
import useUser from '@/hooks/useUser';

const TransactionPinModal = ({
  showModal,
  setShowModal,
  paymentMethod,
  paymentData,
  setPaymentData,
  setShowAccountInfoModal
}) => {
  const [otp, setOtp] = useState('');
  const { disbursedPaymentData } = useSelector(appSelector);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useUser();

  const handleMakePayment = useMutation(({ id, data }) => InvoiceDiscountMakePayment(id, data), {
    onSuccess: (data) => {
      if (paymentMethod === 'wallet') {
        setShowModal(false);
        queryClient.invalidateQueries({ queryKey: ['invoice-discount-disbursed'] });
        queryClient.invalidateQueries({ queryKey: ['invoice-discount-metrics'] });
        toast.success('Payment successful');
        if (disbursedPaymentData?.paymentType === 'full') {
          navigate(`/financing/invoice-discounting?tab=closed&page=1`);
        } else {
          navigate(`/financing/invoice-discounting?tab=disbursed&page=1`);
        }
      } else {
        setShowModal(false);
        setShowAccountInfoModal(true);
        setPaymentData(data?.data);
      }
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message);
    }
  });

  const onSubmit = () => {
    const data = {
      requestId: disbursedPaymentData?.requestId,
      channel: disbursedPaymentData?.channel,
      amount: parseFloat(disbursedPaymentData?.amount),
      pin: otp,
      impersonatingUserId: user?.impersonatingUserId
    };
    handleMakePayment.mutate({ id: disbursedPaymentData?.requestId, data });
  };

  return (
    <>
      <AnimatePresence>
        {showModal && (
          <ModalView
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="modal-overlay">
            <motion.div
              initial={{ y: 1000 }}
              animate={{ y: 0 }}
              exit={{ y: 100 }}
              transition={{ duration: 0.3 }}
              className="modal"
              onClick={(e) => e.stopPropagation()}>
              {/* ======== Content ======== */}
              <ModalContent>
                <ContentView>
                  <div className="header">
                    <h3>Transaction PIN</h3>
                    <button type="button" onClick={() => setShowModal(false)}>
                      <CloseIcon />
                    </button>
                  </div>

                  <div className="content">
                    <TitleView>
                      <p>Enter your transaction PIN to confirm payment</p>
                    </TitleView>

                    <FormView>
                      <div className="input-view">
                        <OTPInput
                          value={otp}
                          onChange={setOtp}
                          numInputs={6}
                          renderSeparator={<span></span>}
                          renderInput={(props) => <input {...props} />}
                          containerStyle="otp-input"
                          inputType="number"
                          skipDefaultStyles
                          shouldAutoFocus
                        />
                      </div>
                      <div className="action-btn">
                        <button
                          type="button"
                          className="btn btn-outline"
                          onClick={() => setShowModal(false)}>
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={otp.length < 6}
                          onClick={onSubmit}>
                          {handleMakePayment.isLoading ? (
                            <Oval color="#fff" height={20} width={20} />
                          ) : (
                            'Make repayment'
                          )}
                        </button>
                      </div>
                    </FormView>
                  </div>
                </ContentView>
              </ModalContent>
            </motion.div>
          </ModalView>
        )}
      </AnimatePresence>
    </>
  );
};

export default TransactionPinModal;

const ModalView = styled(motion.div)`
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9011;
  display: flex;
  align-items: center;
  justify-content: center;

  .modal {
    background-color: ${(props) => props.theme.colors?.white};
    z-index: 30000;
    position: fixed;
    border-radius: 16px;
    width: 550px;
    padding: 30px 0px;
    padding-top: 0;
    padding-bottom: 20px;
    border-radius: 10px;
    min-height: 300px;
    max-height: 90%;
    overflow-y: scroll;

    @media ${device.phone} {
      width: 96%;
    }
  }
`;

const ModalContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: flex-start;
  margin: 0;
  justify-content: flex-start;
  flex-direction: column;
  padding: 20px 30px;

  @media ${device.phone} {
    padding: 20px 16px;
  }

  .header {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;

    h3 {
      font-size: 1.1rem;
      color: ${(props) => props.theme.colors?.secondary};
      font-weight: 600;
    }
  }
`;

const ContentView = styled.div`
  width: 100%;

  .content {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-direction: column;
    margin-top: 30px;

    .warn-view {
      width: 100%;
      display: flex;
      align-items: flex-start;
      justify-content: flex-start;
      background-color: ${({ theme }) => theme.colors.primaryFocus};
      padding: 20px;
      border-radius: 10px;
      margin-bottom: 30px;

      p {
        font-size: 1rem;
        font-weight: 500;
        color: ${({ theme }) => theme.colors.secondary};
        line-height: 20px;
        max-width: 100%;

        span {
          font-weight: 600;
        }
      }
    }
  }
`;

const TitleView = styled.div`
  width: 100%;

  h3 {
    font-size: 1.1rem;
    color: ${(props) => props.theme.colors?.secondary};
    font-weight: 600;
    margin-bottom: 8px;
  }

  p {
    font-size: 1rem;
    color: ${(props) => props.theme.colors?.secondary};
    font-weight: 400;
    margin-bottom: 20px;
  }
`;

const FormView = styled.form`
  width: 100%;

  .input-view {
    width: 100%;
    margin-bottom: 30px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;

    input {
      width: 100%;
    }

    label {
      font-size: 0.875rem;
      font-weight: 400;

      span {
        font-size: 0.75rem;
        color: ${({ theme }) => theme.colors?.info};
      }
    }

    .error-label {
      font-size: 0.875rem;
      color: ${({ theme }) => theme.colors?.error};
      margin-top: 5px;
    }

    .input-info-view {
      width: 100%;
      display: flex;
      margin-top: 5px;

      span {
        font-size: 0.875rem;
        color: ${({ theme }) => theme.colors?.info};
      }
    }

    .otp-input {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: 30px;

      input {
        width: calc(100% / 6 - 10px);
        height: 56px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 10px;
        border: none;
        font-size: 1rem;
        font-weight: 400;
        color: ${({ theme }) => theme.colors?.secondary};
        text-align: center;
        background-color: ${({ theme }) => theme.colors?.inputBackground};
        -webkit-text-security: disc;

        &:focus {
          outline: 1px solid ${({ theme }) => theme.colors?.primary};
          border: none;
        }
      }
    }
  }

  .resend-view {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 30px;

    span {
      font-size: 0.875rem;
      color: ${({ theme }) => theme.colors?.secondary};
      font-weight: 400;
    }

    .resend {
      font-size: 0.875rem;
      color: ${({ theme }) => theme.colors?.primary};
      font-weight: 600;
      cursor: pointer;
      background-color: transparent;
    }
  }

  .action-btn {
    width: 100%;
    display: flex;
    gap: 1rem;
    align-items: center;
    margin-top: 50px;

    button {
      height: 56px;
      border-radius: 10px;
      padding: 12px 32px;
      font-size: 1rem;
      font-weight: 600;

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    .btn-outline {
      width: 40%;
      background-color: transparent;
      border: 1px solid ${({ theme }) => theme.colors?.primary};
      color: ${({ theme }) => theme.colors?.primary};
    }

    .btn-primary {
      width: 60%;
      text-align: center;
      background-color: ${({ theme }) => theme.colors?.primary};
      border: 1px solid ${({ theme }) => theme.colors?.primary};
      color: ${({ theme }) => theme.colors?.white};
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
`;
